import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import Svg, { Path, Circle } from 'react-native-svg';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import {
  fetchDeliveries,
  fetchDeliveryDetail,
  updateDeliveryFeedback,
  fetchEquipmentConfigItems,
  fetchPhotoCategories,
} from '../PerfectTruck/perfectTruckApi';
import {
  DeliveryListItem,
  DeliveryDetail,
  DeliveryStatus,
} from '../PerfectTruck/types';
import { EquipmentConfigItem, PhotoCategoryItem } from '../Admin/pages/PerfectTruckManagement/types';
import type { RootState } from '../../store';
import TruckDropDown from './components/TruckDropDown';
import TruckPhotoGrid from './components/TruckPhotoGrid';
import LhTextInput from '../../components/LhTextInput';
import { useToast } from '../../components/LhToastContainer';
import FeedbackModal from '../../components/FeedbackModal';
import { hasDeliveryRPMFullAccess } from './harborRoles';
import {
  formatDate,
  formatTimeAmPm,
  formatUserName,
} from '../PerfectTruck/perfectTruckUtils';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

interface MyTruckScreenProps {
  onNavigate?: (screen: string, params?: Record<string, any>) => void;
}

type DropdownItem = { id: string | number; displayText: string; [key: string]: any };

interface ParsedInspectionItem {
  group: string;
  items: Array<{
    question: string;
    response: string; // primary value (e.g. "Loaded", "Yes")
    remarks?: string | null; // optional remarks shown under the value
  }>;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const TruckIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path
      d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5l1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2.22-3c-.55-.61-1.33-1-2.22-1s-1.67.39-2.22 1H3V6h12v9H8.22zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
      fill={AdminColors.blue700}
    />
  </Svg>
);

const PhotoIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path
      d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
      fill={AdminColors.blue700}
    />
  </Svg>
);

const ChecklistIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill={AdminColors.blue700}
    />
  </Svg>
);

const EyeIcon = () => {
  const dc = useDarkColor();
  return (
    <Svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke={dc("#005CBC")}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <Path d="M12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z" />
    </Svg>
  );
};

const CheckMarkIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill="#fff"
      stroke="#fff"
      strokeWidth={1.2}
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </Svg>
);

const ErrorIcon = () => (
  <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <Path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#C12C01" />
  </Svg>
);

const CloseIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill={AdminColors.gray500} />
  </Svg>
);

// ─── Pure helpers (no closure dependencies) ───────────────────────────────────

const normalizeResponseKey = (value: unknown): string => String(value ?? '').trim().toLowerCase();

export const parseInspectionData = (
  detail: DeliveryDetail,
  equipmentConfig: EquipmentConfigItem[],
  truckPluggedInLabel?: string,
  yesLabel?: string,
  noLabel?: string,
): ParsedInspectionItem[] => {
  const groups: ParsedInspectionItem[] = [];
  try {
    if (detail.certifierResponse) {
      const certMap = JSON.parse(detail.certifierResponse) as Record<string, unknown>;
      const configById = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.id), item] as const));
      const configByName = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.equipmentName), item] as const));
      const groupMap: Record<string, ParsedInspectionItem> = {};
      Object.entries(certMap).forEach(([key, value]) => {
        const configItem = configById.get(normalizeResponseKey(key)) ?? configByName.get(normalizeResponseKey(key));
        const isZeroKey = normalizeResponseKey(key) === '0';
        const questionText = configItem?.equipmentName ?? (isZeroKey && truckPluggedInLabel ? truckPluggedInLabel : key);
        const groupName = 'Equipment Check';
        if (!groupMap[groupName]) {
          groupMap[groupName] = { group: groupName, items: [] };
        }

        // Normalize response values. Some responses are objects like { value, remarks }.
        let responseText = 'N/A';
        let remarksText: string | null = null;
        if (value === null || value === undefined) {
          responseText = 'N/A';
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          const normalizedValue = normalizeResponseKey(value);
          if (normalizedValue === 'pos') {
            if (configItem) responseText = configItem.certifierPosLabel;
            else if (isZeroKey && yesLabel) responseText = yesLabel;
            else responseText = String(value);
          } else if (normalizedValue === 'neg') {
            if (configItem) responseText = configItem.certifierNegLabel;
            else if (isZeroKey && noLabel) responseText = noLabel;
            else responseText = String(value);
          } else {
            responseText = String(value);
          }
        } else if (typeof value === 'object') {
          if ('value' in value) {
            const storedValue = (value as { value?: unknown }).value;
            const normalizedValue = normalizeResponseKey(storedValue);
            if (normalizedValue === 'pos') {
              if (configItem) responseText = configItem.certifierPosLabel;
              else if (isZeroKey && yesLabel) responseText = yesLabel;
              else responseText = String(storedValue ?? 'N/A');
            } else if (normalizedValue === 'neg') {
              if (configItem) responseText = configItem.certifierNegLabel;
              else if (isZeroKey && noLabel) responseText = noLabel;
              else responseText = String(storedValue ?? 'N/A');
            } else {
              responseText = String(storedValue ?? 'N/A');
            }
            if ((value as { remarks?: unknown }).remarks) {
              remarksText = String((value as { remarks?: unknown }).remarks);
            }
          } else {
            const primitive = Object.values(value).find(v => ['string','number','boolean'].includes(typeof v));
            responseText = primitive !== undefined ? String(primitive) : JSON.stringify(value);
          }
        }

        groupMap[groupName].items.push({
          question: questionText,
          response: responseText,
          remarks: remarksText,
        });
      });
      groups.push(...Object.values(groupMap));
    }
  } catch (e) {
    console.warn('Failed to parse certifier response:', e);
  }
  return groups;
};

const parsePhotoData = (detail: DeliveryDetail): Record<string, string | null> => {
  try {
    if (detail.certifierImageResponse) {
      const imageMap = JSON.parse(detail.certifierImageResponse);
      if (imageMap && typeof imageMap === 'object') {
        return Object.fromEntries(
          Object.entries(imageMap).filter(
            ([, url]) => url === null || typeof url === 'string'
          )
        ) as Record<string, string | null>;
      }
    }
  } catch (e) {
    console.warn('Failed to parse images:', e);
  }
  return {};
};

// Error messages are set via t() inside the component; these constants are replaced.

// ─── My Truck Screen ──────────────────────────────────────────────────────────

const MyTruckScreen: React.FC<MyTruckScreenProps> = ({ onNavigate }) => {
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('harbor');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const currentLanguage = (i18n.language ?? 'en').split('-')[0];
  // Keep a ref to the latest `t` so callbacks that use it don't need `t` as a
  // dependency (which would cause useEffect to re-fire on every language change).
  const tRef = React.useRef(t);
  React.useEffect(() => { tRef.current = t; }, [t]);

  const { showToast } = useToast();
  const todayIso = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [selectedDate, setSelectedDate] = useState<string>(todayIso);
  const [selectedDateItem, setSelectedDateItem] = useState<DropdownItem | null>(
    () => ({ id: todayIso, displayText: formatDate(todayIso) })
  );
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [selectedRouteItem, setSelectedRouteItem] = useState<DropdownItem | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  const [photoSectionWidth, setPhotoSectionWidth] = useState(0);
  const [photoCategories, setPhotoCategories] = useState<PhotoCategoryItem[]>([]);
  const [labelledPhotos, setLabelledPhotos] = useState<Record<string, string | null>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [isServiceUnavailableError, setIsServiceUnavailableError] = useState(false);
  const [deliveryDetail, setDeliveryDetail] = useState<DeliveryDetail | null>(null);
  const [availableRoutes, setAvailableRoutes] = useState<DeliveryListItem[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [equipmentConfig, setEquipmentConfig] = useState<EquipmentConfigItem[]>([]);

  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const roleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const isReadOnlyAccess = !hasDeliveryRPMFullAccess(roleName);

  // ─── Load routes for selected date ───────────────────────────────────────

  const loadRoutesForDate = useCallback(async (date: string) => {
    try {
      setIsLoadingRoutes(true);
      setContentError(null);
      setIsServiceUnavailableError(false);
      // preserve existing selectedRouteId/selectedRouteItem so language changes
      // or equipment config refreshes do not reset user input unexpectedly.

      const response = await fetchDeliveries('all', selectedLocation?.name, 0, 100, undefined, date);

      const routesForDate = response.data.filter(
        (item) => item.deliveryDate === date
      );

      if (routesForDate.length === 0) {
        setContentError(tRef.current('my_truck.error_no_record'));
      }

      setAvailableRoutes(routesForDate);
    } catch (err: any) {
      setContentError(tRef.current('my_truck.error_service_unavailable'));
      setIsServiceUnavailableError(true);
      setAvailableRoutes([]);
    } finally {
      setIsLoadingRoutes(false);
    }
  }, [selectedLocation?.name]); // `t` removed – use tRef to avoid re-firing on language change


  // ─── Fetch equipment config on mount and when language changes ───────────
  useEffect(() => {
    let mounted = true;
    const fetchEquipment = async () => {
      try {
        const items = await fetchEquipmentConfigItems({ language: currentLanguage });
        if (!mounted) return;
        setEquipmentConfig(items || []);
      } catch (e) {
        // keep existing equipmentConfig on error
      }
    };
    fetchEquipment();
    return () => { mounted = false; };
  }, [currentLanguage]);

  // ─── Load delivery detail for selected route ────────────────────────────

  const loadDeliveryDetail = useCallback(async (routeId: string) => {
    try {
      setIsLoading(true);
      setContentError(null);
      setIsServiceUnavailableError(false);
      
      const route = availableRoutes.find((r) => r.id.toString() === routeId);
      if (!route) {
        setContentError(tRef.current('my_truck.error_no_record'));
        setDeliveryDetail(null);
        return;
      }

      const detail = await fetchDeliveryDetail(route.id);
      setDeliveryDetail(detail);
    } catch (err: any) {
      const status = err?.response?.status;
      setDeliveryDetail(null);
      if (status === 404) {
        setContentError(tRef.current('my_truck.error_no_record'));
      } else {
        setContentError(tRef.current('my_truck.error_service_unavailable'));
        setIsServiceUnavailableError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [availableRoutes]); // `t` removed – use tRef to avoid re-firing on language change

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(() => {
    loadRoutesForDate(selectedDate);
  }, [selectedDate, loadRoutesForDate]);

  const pastDates = useMemo(() => {
    const dates: string[] = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  }, []);

  const dateChildList = useMemo(
    () => pastDates.map((date) => ({ id: date, displayText: formatDate(date) })),
    [pastDates]
  );

  const handleDateSelect = useCallback((item: DropdownItem | DropdownItem[] | null) => {
    if (item && !Array.isArray(item)) {
      setSelectedDateItem(item as DropdownItem);
      setSelectedDate(String(item.id));
    }
  }, []);

  const handleRouteSelect = useCallback((item: DropdownItem | DropdownItem[] | null) => {
    if (item && !Array.isArray(item)) {
      const picked = item as DropdownItem;
      setSelectedRouteItem(picked);
      setSelectedRouteId(String(picked.id));
      loadDeliveryDetail(String(picked.id));
    } else {
      setSelectedRouteItem(null);
      setSelectedRouteId('');
    }
  }, [loadDeliveryDetail]);

  const handleRetry = useCallback(() => {
    if (selectedRouteId) {
      void loadDeliveryDetail(selectedRouteId);
      return;
    }
    void loadRoutesForDate(selectedDate);
  }, [selectedRouteId, selectedDate, loadDeliveryDetail, loadRoutesForDate]);

  const handleOpenFeedbackModal = useCallback(() => setShowFeedbackModal(true), []);

  const handleCloseFeedbackModal = useCallback(() => setShowFeedbackModal(false), []);

  const handleFeedbackSave = useCallback(async (payload: any) => {
    if (!deliveryDetail) throw new Error('No Perfect Truck record selected.');
    try {
      const body = {
        feedbackCategory: payload?.feedbackCategory ?? '',
        feedback: payload?.feedback?.trim() ?? '',
      };
      await updateDeliveryFeedback(deliveryDetail.id, body);
      const refreshed = await fetchDeliveryDetail(deliveryDetail.id);
      setDeliveryDetail(refreshed);
      setShowFeedbackModal(false);
      showToast({
        header: t('my_truck.toast_header_success'),
        subtext: t('my_truck.toast_feedback_success'),
        variant: 'success',
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? t('my_truck.error_feedback_submit_failed');
      throw new Error(msg);
    }
  }, [deliveryDetail, showToast]);

  const inspectionData = useMemo(
    () => (deliveryDetail ? parseInspectionData(
      deliveryDetail,
      equipmentConfig,
      t('perfectTruck:truckPluggedIn'),
      t('perfectTruck:review.label_yes'),
      t('perfectTruck:review.label_no'),
    ) : []),
    [deliveryDetail, equipmentConfig, currentLanguage]
  );
  const photos = useMemo(
    () => (deliveryDetail ? parsePhotoData(deliveryDetail) : {}),
    [deliveryDetail]
  );

  // When a delivery is selected or language changes, fetch translated photo
  // categories and map the backend photoCategoryId keys to the localized
  // `photoLabel` values used as the UI caption beneath each thumbnail.
  useEffect(() => {
    let mounted = true;
    const loadAndMap = async () => {
      try {
        const cats = await fetchPhotoCategories(currentLanguage);
        if (!mounted) return;
        setPhotoCategories(cats || []);
        const byId = new Map<string, string>();
        (cats || []).forEach(c => byId.set(String(c.id), c.photoLabel));

        const mapped: Record<string, string | null> = {};
        Object.entries(photos).forEach(([key, uri]) => {
          const label = byId.get(key) ?? key;
          mapped[label] = uri;
        });
        if (mounted) setLabelledPhotos(mapped);
      } catch (e) {
        // On error, fall back to raw keys so the UI still renders something.
        const fallback: Record<string, string | null> = {};
        Object.entries(photos).forEach(([k, uri]) => { fallback[k] = uri; });
        if (mounted) {
          setPhotoCategories([]);
          setLabelledPhotos(fallback);
        }
      }
    };

    // Always attempt to refresh labels when photos exist or language changes.
    if (Object.keys(photos).length > 0) loadAndMap();
    else setLabelledPhotos({});

    return () => { mounted = false; };
  }, [photos, currentLanguage]);

  const routeChildList = useMemo(
    () => availableRoutes.map((route) => ({ id: route.id.toString(), displayText: route.routeId })),
    [availableRoutes]
  );

  // ─── Shared controls JSX ─────────────────────────────────────────────────

  const controlsJsx = (
    <View style={styles.controls}>
      <View style={styles.fieldContainer}>
        <TruckDropDown
          label={t('my_truck.label_delivery_date')}
          placeholder={t('my_truck.placeholder_select_date')}
          childList={dateChildList}
          selectedValue={selectedDateItem}
          setSelectedValue={handleDateSelect}
          selection="single"
          size="medium"
          style={styles.dropdownOverride}
        />
      </View>

      <View style={styles.fieldContainer}>
        <TruckDropDown
          label={t('my_truck.label_route_number')}
          placeholder={isLoadingRoutes ? t('my_truck.placeholder_loading_routes') : availableRoutes.length === 0 ? t('my_truck.placeholder_no_routes') : t('my_truck.placeholder_select_route')}
          childList={routeChildList}
          selectedValue={selectedRouteItem}
          setSelectedValue={handleRouteSelect}
          selection="single"
          isClearable
          readOnly={isLoadingRoutes || availableRoutes.length === 0}
          size="medium"
          style={styles.dropdownOverride}
        />
      </View>
    </View>
  );

  const routeNumberInfoBanner = (
    <View style={styles.infoBanner}>
      <Text style={styles.infoBannerText}>{t('my_truck.route_number_info_banner')}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.pageTitle}>{t('my_truck.title')}</Text>
          {!isReadOnlyAccess && (
            <View style={styles.feedbackButtonGroup}>
              {!!(deliveryDetail?.feedback || deliveryDetail?.feedbackCategory) && (
                <View style={[styles.feedbackPresentBadge, isDark && { backgroundColor: '#1A6B24', borderColor: '#1A6B24' }]}>
                  <Text style={styles.feedbackPresentBadgeText}>{t('my_truck.badge_feedback_submitted')}</Text>
                </View>
              )}
              <TouchableOpacity
                style={[styles.feedbackButton, !deliveryDetail && styles.feedbackButtonDisabled]}
                onPress={handleOpenFeedbackModal}
                disabled={!deliveryDetail}
              >
                <Text style={[styles.feedbackButtonText, !deliveryDetail && styles.feedbackButtonTextDisabled]}>
                  {(deliveryDetail?.feedback || deliveryDetail?.feedbackCategory) ? t('my_truck.action_edit_feedback') : t('my_truck.action_submit_feedback')}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Dropdowns */}
        {controlsJsx}

        {/* Route Number Info Banner */}
        {routeNumberInfoBanner}

        {isReadOnlyAccess && (
          <View style={styles.readOnlyBanner}>
            <EyeIcon />
            <Text style={styles.readOnlyBannerText}>{t('my_truck.readonly_banner')}</Text>
          </View>
        )}

        {/* Asset ID Card */}
        {selectedRouteItem && (
          <View style={styles.assetIdCard}>
            <View style={styles.assetIdIcon}>
              <TruckIcon />
            </View>
            <View style={styles.assetIdContent}>
              <Text style={styles.assetIdLabel}>{t('my_truck.label_asset_id')}</Text>
              <Text style={styles.assetIdValue}>{deliveryDetail?.assetId || 'N/A'}</Text>
              <Text style={styles.assetIdDescription}>{t('my_truck.description_asset_id')}</Text>
            </View>
          </View>
        )}

        {/* Loading state */}
        {isLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={AdminColors.blue700} />
            <Text style={styles.loadingText}>{t('my_truck.loading_truck_data')}</Text>
          </View>
        )}

        {/* Content-area error state */}
        {!!contentError && !isLoading && (
          <View style={styles.errorState}>
            <TruckIcon />
            <Text style={styles.errorTitle}>{t('my_truck.error_title_no_data')}</Text>
            <Text style={styles.errorMessage}>{contentError}</Text>
            {isServiceUnavailableError && (
              <TouchableOpacity
                style={styles.retryButton}
                onPress={handleRetry}
                disabled={isLoadingRoutes || isLoading}
              >
                {(isLoadingRoutes || isLoading) ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.retryButtonText}>{t('my_truck.action_retry')}</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Delivery detail sections */}
        {deliveryDetail && !isLoading && !contentError && (
          <>
            {/* Certification Summary */}
            <View style={styles.certificationSummary}>
              <View style={styles.certCard}>
                <View
                  style={[
                    styles.certIcon,
                    {
                      backgroundColor:
                        deliveryDetail.status === 'Approved'
                          ? AdminColors.green600
                          : AdminColors.yellow200,
                    },
                  ]}
                >
                  {deliveryDetail.status === 'Approved' ? (
                    <CheckMarkIcon />
                  ) : (
                    <Text style={styles.warnIcon}>!</Text>
                  )}
                </View>
                <View style={styles.certContent}>
                  <Text style={styles.certLabel}>{t('my_truck.section_certified_time')}</Text>
                  <Text style={styles.certValue}>
                    {deliveryDetail.certifiedAt
                      ? t('my_truck.certified_time_value', { date: formatDate(deliveryDetail.createdAt), time: formatTimeAmPm(deliveryDetail.certifiedAt) })
                      : t('my_truck.not_yet_certified')}
                  </Text>
                </View>
              </View>

              <View style={styles.certCard}>
                <View
                  style={[
                    styles.certIcon,
                    {
                      backgroundColor: deliveryDetail.certifiedAt
                        ? AdminColors.blue700
                        : AdminColors.gray300,
                    },
                  ]}
                >
                  <Text style={styles.certIconText}>👤</Text>
                </View>
                <View style={styles.certContent}>
                  <Text style={styles.certLabel}>{t('my_truck.section_certified_by')}</Text>
                  <Text style={styles.certValue}>
                    {deliveryDetail.certifierName
                      ? formatUserName(deliveryDetail.certifierName)
                      : t('my_truck.not_yet_certified')}
                  </Text>
                </View>
              </View>
            </View>

            {/* Photos Section */}
            {Object.keys(photos).length > 0 && (
              <View
                style={styles.section}
                onLayout={e => setPhotoSectionWidth(e.nativeEvent.layout.width - 40)}
              >
                <View style={styles.sectionHeader}>
                  <PhotoIcon />
                  <Text style={styles.sectionTitle}>{t('my_truck.section_truck_photos')}</Text>
                </View>
                <Text style={styles.sectionSubtitle}>
                  {t('my_truck.section_truck_photos_sub')}
                </Text>
                <TruckPhotoGrid
                  photos={labelledPhotos}
                  containerWidth={photoSectionWidth}
                />
              </View>
            )}

            {/* Inspection Results Section */}
            {inspectionData.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <ChecklistIcon />
                  <Text style={styles.sectionTitle}>{t('my_truck.section_inspection_results')}</Text>
                </View>
                <Text style={styles.sectionSubtitle}>
                  {t('my_truck.section_inspection_results_sub')}
                </Text>

                {inspectionData.map((group, groupIndex) => (
                  <View key={groupIndex} style={styles.inspectionGroup}>
                    <Text style={styles.groupTitle}>
                      {group.group === 'Equipment Check'
                        ? t('my_truck.inspection_group_equipment_check')
                        : group.group}
                    </Text>
                    {group.items.map((item, itemIndex) => {
                        const configEntry = equipmentConfig.find(
                          (c) => c.equipmentName === item.question
                        );
                        const valLower = item.response.toLowerCase();
                        const isPositive = configEntry
                          ? item.response === configEntry.certifierPosLabel
                          : valLower === 'yes';
                        const isNegative = configEntry
                          ? item.response === configEntry.certifierNegLabel
                          : valLower === 'no';
                        const bgColor = isPositive
                          ? (isDark ? '#1A6B24' : AdminColors.green75)
                          : isNegative
                          ? (isDark ? '#9B2626' : AdminColors.red75)
                          : (isDark ? '#1A5BB5' : AdminColors.blue25);
                        const textColor = isDark
                          ? '#FFFFFF'
                          : isPositive
                          ? AdminColors.green600
                          : isNegative
                          ? AdminColors.red400
                          : AdminColors.blue700;
                        return (
                          <React.Fragment key={itemIndex}>
                            <View style={[styles.inspectionRow, item.remarks ? { borderBottomWidth: 0 } : undefined]}>
                              <Text style={styles.inspectionQuestion}>{item.question}</Text>

                              <View style={{ alignItems: 'flex-end' }}>
                                <View style={[styles.readOnlyVal, { backgroundColor: bgColor }]}>
                                  <Text style={[styles.readOnlyValText, { color: textColor }]}>
                                    {item.response}
                                  </Text>
                                </View>
                              </View>
                            </View>

                            {item.remarks ? (
                              <View style={styles.detailsRow}>
                              <Text style={styles.detailsLabel}>{t('my_truck.inspection_additional_details')}</Text>
                                <LhTextInput
                                    value={item.remarks}
                                    editable={false}
                                    onChangeText={() => {}}
                                    multiline
                                    numberOfLines={3}
                                    style={[styles.multilineInput, styles.detailsFullWidthInput]}
                                />
                              </View>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                  </View>
                ))}
              </View>
            )}

            {/* Certifier Comments Section */}
            {deliveryDetail?.certifierComments && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <ChecklistIcon />
                  <Text style={styles.sectionTitle}>{t('my_truck.section_certifier_comments')}</Text>
                </View>
                <LhTextInput
                  value={deliveryDetail.certifierComments}
                  editable={false}
                  onChangeText={() => {}}
                  multiline
                  numberOfLines={4}
                  style={[styles.multilineInput, styles.commentsDisplayInput]}
                />
              </View>
            )}
          </>
        )}
      </ScrollView>

      <FeedbackModal
        visible={showFeedbackModal}
        mode={(deliveryDetail && (deliveryDetail.feedback || deliveryDetail.feedbackCategory)) ? 'edit' : 'create'}
        initialFeedbackCategory={deliveryDetail?.feedbackCategory}
        initialFeedback={deliveryDetail?.feedback}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleFeedbackSave}
      />

    </View>
  );
};

const baseStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 60,
  },
  breadcrumb: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  breadcrumbLink: {
    color: AdminColors.textLink,
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
  breadcrumbSep: {
    color: AdminColors.gray400,
    fontSize: 14,
    marginHorizontal: 8,
  },
  breadcrumbCurrent: {
    color: AdminColors.textPrimary,
    fontSize: 14,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  feedbackButtonGroup: {
    alignItems: 'flex-end',
    gap: 6,
  },
  feedbackPresentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.green75,
    borderWidth: 1,
    borderColor: AdminColors.green600,
    borderRadius: 12,
    paddingVertical: 3,
    paddingHorizontal: 10,
  },
  feedbackPresentBadgeText: {
    fontSize: 12,
    color: AdminColors.green600,
    fontWeight: '600',
    fontFamily: FontFamily.medium,
  },
  feedbackButton: {
    backgroundColor: AdminColors.blue700,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  feedbackButtonDisabled: {
    backgroundColor: AdminColors.gray300,
  },
  feedbackButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  feedbackButtonTextDisabled: {
    color: AdminColors.gray450,
  },
  controls: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  readOnlyBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#DAEEFA',
    borderWidth: 1,
    borderColor: '#C6DCFB',
    borderRadius: 8,
    padding: 10,
    paddingHorizontal: 14,
    marginBottom: 20,
  },
  readOnlyBannerText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    color: '#01346F',
    fontFamily: FontFamily.medium,
  },
  infoBanner: {
    backgroundColor: '#E8F4F8',
    borderWidth: 1,
    borderColor: '#B8D4E0',
    borderRadius: 8,
    padding: 12,
    paddingHorizontal: 14,
    marginBottom: 20,
  },
  infoBannerText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#005084',
    fontFamily: FontFamily.medium,
    lineHeight: 18,
  },
  fieldContainer: {
    flex: 1,
  },
  // Override LHDropDown's default marginBottom so controls row has consistent spacing
  dropdownOverride: {
    marginBottom: 0,
    width: '100%',
  },
  assetIdCard: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
    alignItems: 'flex-start',
    gap: 12,
  },
  assetIdIcon: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: AdminColors.blue75,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  assetIdContent: {
    flex: 1,
    justifyContent: 'center',
  },
  assetIdLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
    fontFamily: FontFamily.bold,
  },
  assetIdValue: {
    fontSize: 16,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    marginBottom: 2,
    fontFamily: FontFamily.bold,
  },
  assetIdDescription: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  loadingContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 40,
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 14,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  errorState: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 40,
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  errorMessage: {
    fontSize: 14,
    color: AdminColors.textSecondary,
    textAlign: 'center',
    fontFamily: FontFamily.regular,
  },
  retryButton: {
    marginTop: 8,
    minWidth: 110,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    backgroundColor: AdminColors.blue700,
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  certificationSummary: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  certCard: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 14,
    gap: 12,
  },
  certIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  certIconText: {
    fontSize: 20,
  },
  warnIcon: {
    fontSize: 24,
    color: '#fff',
    fontWeight: 'bold',
  },
  certContent: {
    flex: 1,
    justifyContent: 'center',
  },
  certLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
    fontFamily: FontFamily.bold,
  },
  certValue: {
    fontSize: 15,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    marginBottom: 16,
    fontFamily: FontFamily.regular,
  },
  inspectionGroup: {
    marginBottom: 16,
  },
  groupTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    marginBottom: 8,
    fontFamily: FontFamily.bold,
  },
  inspectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  inspectionQuestion: {
    fontSize: 14,
    color: AdminColors.textPrimary,
    flex: 1,
    fontFamily: FontFamily.regular,
  },
  readOnlyVal: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
  },
  readOnlyValText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
  },
  detailsLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.textMuted,
    marginBottom: 6,
  },
  multilineInput: {
    minHeight: 64,
    textAlignVertical: 'top',
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    padding: 12,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    backgroundColor: '#fff',
  },
  detailsRow: {
    marginTop: 8,
    marginBottom: 12,
  },
  detailsFullWidthInput: {
    width: '100%',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 430,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  modalCloseButton: {
    padding: 8,
  },
  feedbackInput: {
    minHeight: 96,
    padding: 12,
    margin: 20,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    fontSize: 14,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    textAlignVertical: 'top',
  },
  characterCount: {
    fontSize: 12,
    color: AdminColors.textMuted,
    marginHorizontal: 20,
    marginBottom: 16,
    fontFamily: FontFamily.regular,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
  },
  cancelButtonText: {
    color: AdminColors.textPrimary,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  submitButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: AdminColors.blue700,
  },
  submitButtonDisabled: {
    backgroundColor: AdminColors.gray50,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  feedbackErrorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginHorizontal: 20,
    marginBottom: 12,
  },
  feedbackErrorText: {
    flex: 1,
    fontSize: 13,
    color: '#C12C01',
    fontFamily: FontFamily.regular,
  },
  commentsDisplayInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
});

export default MyTruckScreen;
